'use client';
import ConnectedIcon from '@/components/icons/Connected';

import Button from '@/components/Button';
import Refresh from '@/components/icons/Refresh';
import AccountSection from '@/components/layout/AccountSection';
import Footer from '@/components/layout/Footer';
import ProfileDropdown from '@/components/layout/ProfileDropdown';
import Toast from '@/components/layout/Toast';
import ResetPasswordForm from '@/components/ResetPasswordForm';
import Spinner from '@/components/Spinner';
import useNavigate from '@/hooks/useNavigate';
import { isLoggedInToNextAndApi_Action } from '@/services/connexion.actions';
import Errors from '@/services/errors';
import { AnimatePresence, motion } from 'motion/react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { createContext, lazy, Suspense, useEffect, useState } from 'react';

const LoginForm = lazy(() => import('@/components/LoginForm'));
const NavList = lazy(() => import('@/components/layout/NavList'));

export const BootstrapContext = createContext();
export const ToastContext = createContext();
export const ConnectedStateContext = createContext();
export const IsMobileMediaContext = createContext();
export const IsTabletMediaContext = createContext();
export const IsDesktopMediaContext = createContext();
export const ConnectionModalOpenContext = createContext();

const Layout = ({ children }) => {
  const searchParams = useSearchParams();
  const [bootstrap, setBootstrap] = useState(null);
  const mobileBreakPoint = 768;
  const tabletBreakPoint = 992;
  const navigate = useNavigate();

  // Check if running in the browser
  const isBrowser = typeof window !== 'undefined';

  const loginOpen = searchParams.get('login-open');

  const [isClient, setIsClient] = useState(false);

  const [windowWidth, setWindowWidth] = useState(isBrowser ? window.innerWidth : 0);
  const [isMobileMedia, setIsMobileMedia] = useState(
    isBrowser ? window.innerWidth <= mobileBreakPoint : false
  );
  const [isTabletMedia, setIsTabletMedia] = useState(
    isBrowser ? window.innerWidth <= tabletBreakPoint && window.innerWidth > mobileBreakPoint : false
  );
  const [isDesktopMedia, setIsDesktopMedia] = useState(
    isBrowser ? window.innerWidth > tabletBreakPoint : false
  );
  const [isConnected, setIsConnected] = useState(false);
  const [showLogin, setShowLogin] = useState(true);
  const [toast, setToast] = useState({ message: '', type: '' });
  const connectionModalOpenState = useState(false);

  const updateToast = ({ message, type }) => {
    setToast({ message, type });
  };

  const handleResize = () => {
    if (isBrowser) {
      setIsMobileMedia(window.innerWidth <= mobileBreakPoint);
      setIsTabletMedia(window.innerWidth <= tabletBreakPoint && window.innerWidth > mobileBreakPoint);
      setIsDesktopMedia(window.innerWidth > tabletBreakPoint);
    }
  };
  const fetchLoggedInStatus = async () => {
    try {
      // const isLoggedIn = true;
      const isLoggedIn = await isLoggedInToNextAndApi_Action();
      setIsConnected((isConnected) => isLoggedIn);
    } catch (e) {
      Errors.handleError(e, updateToast);
    }
  };

  const handleSignupClick = () => {
    navigate('/conditions-generales-utilisation?subscribe=1');
  };
  const handlePasswordForgotClick = () => {
    setShowLogin(false);
  };
  const handleModalReset = () => {
    setShowLogin(true);
  };

  useEffect(() => {
    setIsClient(true);
    if (isBrowser) {
      const loginModal = document.getElementById('login-modal');
      loginModal?.addEventListener('hidden.bs.modal', handleModalReset);

      // activate tooltips

      return () => {
        loginModal?.removeEventListener('hidden.bs.modal', handleModalReset);
      };
    }
  }, []);
  useEffect(() => {
    if (isBrowser) {
      window.addEventListener('resize', handleResize);
      handleResize();
    }

    return () => {
      if (isBrowser) {
        window.removeEventListener('resize', handleResize);
      }
    };
  }, []);

  useEffect(() => {
    if (isBrowser) {
      fetchLoggedInStatus();
    }
  }, []);

  useEffect(() => {
    const loadBootstrap = async () => {
      const bootstrapModule = await import('bootstrap/dist/js/bootstrap.bundle.min');
      setBootstrap(bootstrapModule);
    };

    loadBootstrap();

    // Cleanup if necessary
    return () => {
      // Any cleanup logic if needed
    };
  }, []);

  useEffect(() => {
    if (loginOpen && bootstrap) {
      bootstrap.Modal.getOrCreateInstance('#login-modal').show();
    }
  }, [loginOpen, bootstrap]);

  useEffect(() => {
    if (!connectionModalOpenState[0]) {
      // reset connection Modal states when closed
      setShowLogin(true);
    }
  }, [connectionModalOpenState[0]]);

  return (
    <>
      <BootstrapContext.Provider value={bootstrap}>
        <IsDesktopMediaContext.Provider value={isDesktopMedia}>
          <IsTabletMediaContext.Provider value={isTabletMedia}>
            <IsMobileMediaContext.Provider value={isMobileMedia}>
              <ToastContext.Provider value={updateToast}>
                <ConnectedStateContext.Provider value={[isConnected, setIsConnected]}>
                  <ConnectionModalOpenContext.Provider value={connectionModalOpenState}>
                    <div className="modal fade notranslate" id={'login-modal'} tabIndex="-1">
                      <div className="modal-dialog">
                        <div className="modal-content">
                          <div className="modal-header">
                            <h5 className="modal-title">{showLogin ? 'Connexion' : 'Mot de passe oublié'}</h5>
                            <Button
                              type="button"
                              className="btn-close"
                              data-bs-dismiss="modal"
                              aria-label="Close"
                              onClick={() => connectionModalOpenState[1](false)}
                            ></Button>
                          </div>
                          <div className="modal-body">
                            <Suspense fallback={<Spinner />}>
                              <AnimatePresence>
                                {showLogin ? (
                                  <motion.div
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -10 }}
                                    transition={{ duration: 0.3 }}
                                  >
                                    <LoginForm />
                                  </motion.div>
                                ) : (
                                  <motion.div
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -10 }}
                                    transition={{ duration: 0.3 }}
                                  >
                                    <ResetPasswordForm />
                                  </motion.div>
                                )}
                              </AnimatePresence>
                            </Suspense>
                          </div>
                          {showLogin && (
                            <div className="modal-footer">
                              <div className="footer-container_bac row justify-content-md-end align-items-md-start">
                                <div className="col-12 col-md-auto py-1">
                                  <Link
                                    href="/conditions-generales-utilisation?subscribe=1"
                                    className="btn btn-outline-success w-100 "
                                    onClick={handleSignupClick}
                                    data-bs-dismiss="modal"
                                  >
                                    S&apos;inscrire
                                  </Link>
                                </div>
                                <div className="col-12 col-md-auto py-1">
                                  <Button
                                    type="button"
                                    className="btn btn-outline-primary w-100"
                                    onClick={handlePasswordForgotClick}
                                  >
                                    Mot de passe oublié
                                  </Button>
                                </div>
                                <div className="col-12 col-md-auto py-1">
                                  <Button type="submit" form="login-form" className="btn btn-primary w-100">
                                    Se connecter
                                  </Button>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <header className={'page-header notranslate'}>
                      <nav className="navbar navbar-expand-lg bg-body-tertiary nav-top py2">
                        <div className="container-fluid">
                          <div
                            className={`d-flex ${
                              isMobileMedia && isClient ? 'w-100 justify-content-between' : 'w-75'
                            }`}
                          >
                            <Link
                              className="navbar-brand h-100 d-flex align-items-center me-1 me-md-3 max-w-40"
                              href="/"
                            >
                              <img
                                src="/images/Bingo_Wendake_Mini.png"
                                className="brand-logo py-1 max-w-100 h-auto object-fit-contain"
                                data-bs-toggle="tooltip"
                                data-bs-placement="bottom"
                                title="Revenir à la page d'accueil"
                                alt="bingo logo"
                              />
                            </Link>
                            <div
                              className="status-icons w-25 w-md-20 w-xl-10
                            d-flex align-items-center justify-content-between justify-content-md-start"
                            >
                              <Button
                                className={'d-none d-sm-block w-30 w-md-50 w-xl-50  btn p-0 m-2'}
                                onClick={() => {
                                  window.location.reload();
                                }}
                                // tooltip="Rafraîchir la page"
                                data-bs-toggle="tooltip"
                                data-bs-placement="bottom"
                                title="Rafraîchir la page"
                              >
                                <Refresh fillColor={'primary'} className={'w-100'} />
                              </Button>
                              {isMobileMedia && isClient ? (
                                <motion.div
                                  initial={{ opacity: 0, height: 0 }}
                                  animate={{ opacity: 1, height: 'auto' }}
                                  exit={{ opacity: 0, height: 0 }}
                                  className=" w-sm-30 w-md-50 w-xl-35 d-flex"
                                >
                                  <ConnectedIcon
                                    fillColor={isConnected ? 'warning' : 'light'}
                                    className={`w-100 min-w-50  ${isConnected ? '' : 'd-none'}`}
                                  />
                                  <ProfileDropdown />
                                </motion.div>
                              ) : (
                                <div
                                  className="w-50 w-sm-30 w-md-50 w-xl-35 m-2"
                                  data-bs-toggle="tooltip"
                                  data-bs-placement="bottom"
                                  title="Statut de connexion"
                                >
                                  <ConnectedIcon
                                    fillColor={isConnected ? 'warning' : 'light'}
                                    className={'w-100'}
                                  />
                                </div>
                              )}
                            </div>
                            <Button
                              className="btn navbar-toggler"
                              type="button"
                              data-bs-toggle="collapse"
                              data-bs-target="#navbarSupportedContent"
                              aria-controls="navbarSupportedContent"
                              aria-expanded="false"
                              aria-label="Toggle navigation"
                              // onClick={(e)=>{handleToggleNavClick(e)}}
                            >
                              <span className="navbar-toggler-icon"></span>
                            </Button>
                            <Suspense fallback={<Spinner />}>
                              {!(isMobileMedia || isTabletMedia) && isClient && (
                                <div className="collapse navbar-collapse" id="navbarSupportedContent">
                                  <NavList />
                                  {isMobileMedia && <AccountSection islogged={isConnected} />}
                                </div>
                              )}
                            </Suspense>
                          </div>

                          {!isMobileMedia && isClient && (
                            <div className="w-25 d-flex justify-content-end">
                              <AccountSection islogged={isConnected} />
                            </div>
                          )}

                          <Suspense fallback={<Spinner />}>
                            {(isMobileMedia || isTabletMedia) && isClient && (
                              <div
                                className="collapse navbar-collapse bg-body-tertiary"
                                id="navbarSupportedContent"
                              >
                                <NavList />
                                {isMobileMedia && (
                                  <>
                                    <div className="py-2">
                                      <AccountSection islogged={isConnected} isMobile={isMobileMedia} />
                                    </div>
                                    <div className="pt-2">
                                      <iframe
                                        src="https://mixlr.com/users/8847859/embed"
                                        width="100%"
                                        scrolling="no"
                                        frameBorder="no"
                                        marginHeight="0"
                                        marginWidth="0"
                                      ></iframe>
                                    </div>
                                  </>
                                )}
                              </div>
                            )}
                          </Suspense>
                        </div>
                      </nav>
                    </header>
                    <main className="container-fluid page-main notranslate">
                      {children}
                      <Toast message={toast.message.toString()} type={toast.type} />
                    </main>
                    <Footer isMobile={isMobileMedia} isTablet={isTabletMedia} />
                  </ConnectionModalOpenContext.Provider>
                </ConnectedStateContext.Provider>
              </ToastContext.Provider>
            </IsMobileMediaContext.Provider>
          </IsTabletMediaContext.Provider>
        </IsDesktopMediaContext.Provider>
      </BootstrapContext.Provider>
    </>
  );
};

export default Layout;
